import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Input,
	Select,
	Row,
	Col,
	Typography,
	Badge,
	Tooltip,
	notification,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiMagnifyingGlassDuotone,
	PiCreditCardDuotone,
	PiEyeDuotone,
	PiCalendarDuotone,
	PiCurrencyDollarDuotone,
	PiReceiptDuotone,
	PiUserDuotone,
	PiHouseDuotone,
} from "react-icons/pi";
import { requestGetTransactionList } from "../../services/admin.service";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

function TransactionListPage() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [transactions, setTransactions] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [searchKeyword, setSearchKeyword] = useState(searchParams.get("keyword") || "");
	const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
	const [methodFilter, setmethodFilter] = useState(searchParams.get("method") || "");

	useEffect(() => {
		document.title = "Cloudiva Console | Transactions";
		fetchTransactions();
	}, [pagination.current, pagination.pageSize, searchKeyword, statusFilter, methodFilter]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (searchKeyword) params.set("keyword", searchKeyword);
		if (statusFilter) params.set("status", statusFilter);
		if (methodFilter) params.set("method", methodFilter);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [
		pagination.current,
		pagination.pageSize,
		searchKeyword,
		statusFilter,
		methodFilter,
		setSearchParams,
	]);

	const fetchTransactions = () => {
		setLoading(true);
		const filterParts = [];

		if (statusFilter) filterParts.push(`status:${statusFilter}`);
		if (methodFilter) filterParts.push(`method:${methodFilter}`);

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: "",
			filter: filterParts.join(","),
			order: "_id:desc",
		};

		requestGetTransactionList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const transactionsData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setTransactions(transactionsData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				console.error("Error fetching transactions:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch transactions",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleTableChange = (newPagination) => {
		setPagination({
			...pagination,
			current: newPagination.current,
			pageSize: newPagination.pageSize,
		});
	};

	const handleSearch = (value) => {
		setSearchKeyword(value);
		setPagination({ ...pagination, current: 1 });
	};

	const handleStatusFilter = (value) => {
		setStatusFilter(value);
		setPagination({ ...pagination, current: 1 });
	};

	const handlemethodFilter = (value) => {
		setmethodFilter(value);
		setPagination({ ...pagination, current: 1 });
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "COMPLETED":
			case "SUCCESS":
				return "green";
			case "FAILED":
			case "CANCELLED":
				return "red";
			case "PROCESSING":
				return "blue";
			default:
				return "default";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Pending";
			case "COMPLETED":
			case "SUCCESS":
				return "Completed";
			case "FAILED":
				return "Failed";
			case "CANCELLED":
				return "Cancelled";
			case "PROCESSING":
				return "Processing";
			default:
				return status;
		}
	};

	const getCategoryColor = (category) => {
		switch (category) {
			case "PAYMENT":
				return "green";
			case "REFUND":
				return "red";
			case "ADJUSTMENT":
				return "blue";
			default:
				return "default";
		}
	};

	const columns = [
		{
			title: "Transaction",
			dataIndex: "number",
			key: "number",
			width: 200,
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500, marginBottom: 4 }}>{text}</div>
					<div style={{ fontSize: "12px", color: "#666" }}>
						{dayjs(record.createdAt).format("DD MMM YYYY, HH:mm")}
					</div>
				</div>
			),
		},
		{
			title: "Team",
			dataIndex: ["team", "name"],
			key: "team",
			width: 150,
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiUserDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{text || "N/A"}</div>
						{record.team?.currency && (
							<div style={{ fontSize: "12px", color: "#666" }}>{record.team.currency}</div>
						)}
					</div>
				</div>
			),
		},
		{
			title: "Invoice",
			dataIndex: "invoiceId",
			key: "invoice",
			width: 150,
			render: (invoiceId, record) => (
				<div>
					{record.metadata?.invoiceNumber ? (
						<div style={{ display: "flex", alignItems: "center" }}>
							<PiReceiptDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
							<Button
								type="link"
								size="small"
								style={{ padding: 0, height: "auto" }}
								onClick={() => navigate(`/invoices/${invoiceId}`)}>
								{record.metadata.invoiceNumber}
							</Button>
						</div>
					) : (
						<Text type="secondary">No invoice</Text>
					)}
				</div>
			),
		},
		{
			title: "Amount",
			dataIndex: "amount",
			key: "amount",
			width: 120,
			align: "right",
			render: (amount, record) => (
				<div style={{ textAlign: "right" }}>
					<div style={{ fontWeight: 600, fontSize: "14px" }}>
						{formatCurrency(amount, record.currency)}
					</div>
					<Tag color={getCategoryColor(record.category)} size="small">
						{record.category}
					</Tag>
				</div>
			),
		},
		{
			title: "Payment Method",
			dataIndex: "method",
			key: "method",
			width: 130,
			render: (method) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiCreditCardDuotone size={16} style={{ marginRight: 8, color: "#722ed1" }} />
					<span>{method?.replace("_", " ") || "N/A"}</span>
				</div>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (status) => <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>,
		},
		{
			title: "Actions",
			key: "actions",
			width: 80,
			align: "right",
			render: (_, record) => (
				<Tooltip title="View Details">
					<Button
						type="text"
						icon={<PiEyeDuotone />}
						onClick={() => navigate(`/transactions/${record._id}`)}
					/>
				</Tooltip>
			),
		},
	];

	const expandedRowRender = (record) => {
		return (
			<div style={{ padding: "12px 0" }}>
				<Row gutter={24}>
					<Col span={8}>
						<div style={{ marginBottom: 8 }}>
							<Text strong>Transaction ID:</Text>
						</div>
						<div style={{ fontSize: "12px", color: "#666", fontFamily: "monospace" }}>
							{record._id}
						</div>
					</Col>
					<Col span={8}>
						<div style={{ marginBottom: 8 }}>
							<Text strong>Created:</Text>
						</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							{dayjs(record.createdAt).format("DD MMMM YYYY, HH:mm:ss")}
						</div>
					</Col>
					<Col span={8}>
						<div style={{ marginBottom: 8 }}>
							<Text strong>Last Updated:</Text>
						</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							{dayjs(record.updatedAt).format("DD MMMM YYYY, HH:mm:ss")}
						</div>
					</Col>
				</Row>
				{record.metadata && Object.keys(record.metadata).length > 0 && (
					<div style={{ marginTop: 12 }}>
						<Text strong>Metadata:</Text>
						<div
							style={{
								backgroundColor: "#f5f5f5",
								padding: "8px",
								borderRadius: "4px",
								marginTop: 4,
								fontSize: "12px",
							}}>
							<pre style={{ margin: 0 }}>{JSON.stringify(record.metadata, null, 2)}</pre>
						</div>
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Space>
						<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
						<Title level={4} style={{ margin: 0 }}>
							Transaction Management
						</Title>
					</Space>
					<Badge count={transactions.length} showZero />
				</Space>
			</div>

			<Card>
				{/* Filters */}
				<div style={{ marginBottom: 16 }}>
					<Row gutter={16}>
						<Col xs={24} sm={12} md={8} lg={6}>
							<Search
								placeholder="Search transactions..."
								allowClear
								onSearch={handleSearch}
								style={{ width: "100%" }}
								prefix={<PiMagnifyingGlassDuotone />}
							/>
						</Col>
						<Col xs={24} sm={12} md={8} lg={6}>
							<Select
								placeholder="Filter by status"
								allowClear
								style={{ width: "100%" }}
								onChange={handleStatusFilter}
								value={statusFilter || undefined}>
								<Option value="PENDING">Pending</Option>
								<Option value="PROCESSING">Processing</Option>
								<Option value="COMPLETED">Completed</Option>
								<Option value="SUCCESS">Success</Option>
								<Option value="FAILED">Failed</Option>
								<Option value="CANCELLED">Cancelled</Option>
							</Select>
						</Col>
						<Col xs={24} sm={12} md={8} lg={6}>
							<Select
								placeholder="Filter by payment method"
								allowClear
								style={{ width: "100%" }}
								onChange={handlemethodFilter}
								value={methodFilter || undefined}>
								<Option value="VIRTUAL_ACCOUNT">Virtual Account</Option>
								<Option value="CASH">Cash</Option>
							</Select>
						</Col>
					</Row>
				</div>

				{/* Table */}
				<Table
					columns={columns}
					dataSource={transactions}
					rowKey="_id"
					loading={loading}
					pagination={{
						...pagination,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`,
					}}
					onChange={handleTableChange}
					expandable={{
						expandedRowRender,
						expandRowByClick: false,
					}}
					scroll={{ x: 800 }}
				/>
			</Card>
		</div>
	);
}

export default TransactionListPage;
